/**
 * I-5F-2 — 6-digit OTP input with auto-advance + paste handling.
 *
 * - Numeric inputs (mobile shows number-pad)
 * - Auto-advance to next field on digit entry
 * - Backspace moves to previous field if current is empty
 * - Paste of full 6-digit code auto-fills all fields + triggers complete
 * - Fixed LTR direction regardless of page direction (digits read LTR even in RTL)
 */
import { useRef, useEffect } from 'react'

interface Props {
  value: string
  onChange: (v: string) => void
  onComplete?: (v: string) => void
  length?: number
  disabled?: boolean
  autoFocus?: boolean
}

export function OtpInput({
  value, onChange, onComplete, length = 6, disabled = false, autoFocus = true,
}: Readonly<Props>) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  const setDigit = (i: number, d: string) => {
    const sanitized = d.replace(/\D/g, '').slice(0, 1)
    const next = digits.slice()
    next[i] = sanitized
    const joined = next.join('')
    onChange(joined)
    if (sanitized && i < length - 1) refs.current[i + 1]?.focus()
    if (joined.length === length && !joined.includes('')) {
      onComplete?.(joined)
    }
  }

  const handleKeyDown = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus()
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus()
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    onChange(pasted)
    const lastFilled = Math.min(pasted.length, length) - 1
    refs.current[lastFilled]?.focus()
    if (pasted.length === length) onComplete?.(pasted)
  }

  return (
    // dir=ltr forces left-to-right layout even when the page is RTL
    // — phone numbers and OTP codes read LTR universally.
    <div dir="ltr" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={d}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          data-testid={`otp-digit-${i}`}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          style={{
            width: 44, height: 52, textAlign: 'center', fontSize: 22,
            fontWeight: 600, borderRadius: 8, border: '1px solid #d4d4d8',
            background: disabled ? '#f4f4f5' : '#fff',
            color: '#18181b',
          }}
        />
      ))}
    </div>
  )
}
